import { type MouseEvent, memo } from 'react';

import { PlayingCardsFanIcon } from '@air/design-icons';

import { WorkspaceAgentGuidedVariantSizeOptionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeOptionRow';
import {
  isWorkspaceAgentSmartResizeSizeSelected,
  sizeSetIsFullySelected,
  type WorkspaceAgentSmartResizeSearchResults,
  type WorkspaceAgentSmartResizeSize,
  workspaceAgentSmartResizeChannelToSize,
  workspaceAgentSmartResizeSavedSizeToOptionRowChannel,
  workspaceAgentSmartResizeShapeOptionToOptionRowChannel,
  workspaceAgentSmartResizeSizeSetToOptionRowChannel,
  workspaceAgentSmartResizeSizeText,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentGuidedVariantSmartResizeSearchResultsProps = {
  hasSearchResults: boolean;
  onAddTyped: () => void;
  onSelectSet: (event: MouseEvent<HTMLButtonElement>) => void;
  onSelectSize: (event: MouseEvent<HTMLButtonElement>) => void;
  search: WorkspaceAgentSmartResizeSearchResults;
  sizes: readonly WorkspaceAgentSmartResizeSize[];
};

export const WorkspaceAgentGuidedVariantSmartResizeSearchResults = memo(
  ({
    hasSearchResults,
    onAddTyped,
    onSelectSet,
    onSelectSize,
    search,
    sizes,
  }: WorkspaceAgentGuidedVariantSmartResizeSearchResultsProps) => (
    <fieldset aria-label="Results" className="m-0 flex min-w-0 flex-col border-0 p-0">
      {search.typed ? (
        <WorkspaceAgentGuidedVariantSizeOptionRow
          channel={{
            height: search.typed.height,
            id: 'add-typed-size',
            label: workspaceAgentSmartResizeSizeText(search.typed),
            ratio: '',
            shortLabel: workspaceAgentSmartResizeSizeText(search.typed),
            width: search.typed.width,
          }}
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_ADD_TYPED"
          isSelected={false}
          label={
            <>
              <span className="ads:text-metadata text-gray-9">Add </span>
              {workspaceAgentSmartResizeSizeText(search.typed)}
            </>
          }
          meta={null}
          onClick={onAddTyped}
        />
      ) : null}
      {search.shapes.map((shape) => {
        const { channel, meta } = workspaceAgentSmartResizeShapeOptionToOptionRowChannel(shape);
        const isSelected = isWorkspaceAgentSmartResizeSizeSelected(sizes, shape.size);

        return (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={channel}
            isSelected={isSelected}
            key={shape.id}
            meta={meta ?? workspaceAgentSmartResizeSizeText(shape.size)}
            onClick={onSelectSize}
          />
        );
      })}
      {search.sets.map((sizeSet) => {
        const { channel, meta } = workspaceAgentSmartResizeSizeSetToOptionRowChannel(sizeSet);
        const isSelected = sizeSetIsFullySelected(sizes, sizeSet);

        return (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={channel}
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SET"
            isSelected={isSelected}
            key={sizeSet.id}
            leading={
              <PlayingCardsFanIcon aria-hidden className="shrink-0 ads:text-icon-subtle text-gray-9" size="sm" />
            }
            meta={meta}
            onClick={onSelectSet}
            setId={sizeSet.id}
          />
        );
      })}
      {search.savedSizes.map((saved) => {
        const { channel, meta } = workspaceAgentSmartResizeSavedSizeToOptionRowChannel(saved);
        const isSelected = isWorkspaceAgentSmartResizeSizeSelected(sizes, saved.size);

        return (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={channel}
            isSelected={isSelected}
            key={saved.id}
            meta={meta}
            onClick={onSelectSize}
          />
        );
      })}
      {search.channels.map((channel) => (
        <WorkspaceAgentGuidedVariantSizeOptionRow
          channel={channel}
          isSelected={isWorkspaceAgentSmartResizeSizeSelected(sizes, workspaceAgentSmartResizeChannelToSize(channel))}
          key={channel.id}
          onClick={onSelectSize}
        />
      ))}
      {!hasSearchResults ? (
        <p className="px-2 py-1.5 ads:text-metadata text-14 text-gray-9">
          No sizes match. Try TikTok, 4:5, or 1080x1350.
        </p>
      ) : null}
    </fieldset>
  ),
);

WorkspaceAgentGuidedVariantSmartResizeSearchResults.displayName = 'WorkspaceAgentGuidedVariantSmartResizeSearchResults';
